import React, { createContext, useContext, useEffect, useState } from 'react';
import QueueService from 'views/Report/Callcenter/Autentication/Services/QueueService';

/**
 * @typedef {Object} countersChatMetrics
 * @property {Number} agentWarning
 * @property {Number} agentCritical
 * @property {Number} customerCritical
 * @property {Number} customerWarning
 * @property {Number} withAgentResponse
 */

/**
 * @typedef {Function} setCounterChatMetrics
 * @param {countersChatMetrics} countersChatMetrics
 */

const QueueContentChatContext = createContext();

export const DefaultFilters = {
  FILA: [],
  TAG: [],
  STATUS: [],
  TIME: [],
  START_TYPE: [],
  CHATS_SAVED: false,
  INTERACTION_AGENT_WARNING: false,
  INTERACTION_AGENT_CRITICAL: false,
  INTERACTION_CLIENT: false,
  CHATS_WITH_RESPONSE_AGENT: false
};

const DefaultSort = {
  MORE_TIME_IN_QUEUE: false,
  LAST_MESSAGE_CUSTOMER: false,
  MORE_TIME_IN_WAIT: false,
  CHATS_SAVED: false
};

const getUserSpecificKey = (baseKey, userId) => {
  return `user_${userId}_${baseKey}`;
};

const storageHelper = {
  get: (key, userId) => {
    try {
      const userKey = getUserSpecificKey(key, userId);
      const item = window.localStorage.getItem(userKey);
      return item ? JSON.parse(item) : null;
    } catch (error) {
      console.error(`Erro ao obter ${key} do localStorage:`, error);
      return null;
    }
  },

  set: (key, value, userId) => {
    try {
      const userKey = getUserSpecificKey(key, userId);
      window.localStorage.setItem(userKey, JSON.stringify(value));
    } catch (error) {
      console.error(`Erro ao salvar ${key} no localStorage:`, error);
    }
  }
};

export function QueueContentChatProvider({ children }) {
  const [allChats, setAllChats] = useState([]);
  const [chatsSaved, setChatsSaved] = useState(new Set());
  const [filters, setFilters] = useState(DefaultFilters);
  const [sort, setSort] = useState(DefaultSort);
  const [countChatsInAttendance, setCountChatsInAttendance] = useState(0);
  const [countChatsInWait, setCountChatsInWait] = useState(0);
  const [countAttributedChats, setCountAttributedChats] = useState(0);
  const [queues, setQueues] = useState([]);
  const userId = localStorage.getItem('login');
  const [
    chatsAgentWithoutInteractionCritical,
    setChatsAgentWithoutInteractionCritical
  ] = useState(new Set());

  const [countersChatMetrics, setCounterChatMetrics] = useState({
    agentWarning: 0,
    agentCritical: 0,
    customerCritical: 0,
    customerWarning: 0,
    withAgentResponse: 0
  });

  useEffect(() => {
    if (!userId) return;

    const filtersChats = storageHelper.get('filters-chat-attendance', userId);
    if (filtersChats) {
      setFilters(filtersChats);
    } else {
      storageHelper.set('filters-chat-attendance', DefaultFilters, userId);
    }

    const sortChats = storageHelper.get('sort-chat-attendance', userId);
    if (sortChats) {
      setSort(sortChats);
    } else {
      storageHelper.set('sort-chat-attendance', DefaultSort, userId);
    }

    const savedChats = storageHelper.get('chatsSaved', userId);
    if (savedChats) {
      setChatsSaved(new Set(savedChats));
    }
  }, [userId]);

  useEffect(() => {
    if (!userId || !allChats.length) return;

    const savedChats = storageHelper.get('chatsSaved', userId) || [];
    const validSavedChats = savedChats.filter((chatId) =>
      allChats.some((chat) => chat.id === chatId || chat._id === chatId)
    );

    if (validSavedChats.length !== savedChats.length) {
      storageHelper.set('chatsSaved', validSavedChats, userId);
      setChatsSaved(new Set(validSavedChats));
    }

    const validChatsAgentWithoutInteractionCritical = Array.from(
      chatsAgentWithoutInteractionCritical
    ).filter((chatId) => allChats.some((chat) => chat.id === chatId));

    if (
      validChatsAgentWithoutInteractionCritical.length !==
      chatsAgentWithoutInteractionCritical.size
    ) {
      setChatsAgentWithoutInteractionCritical(
        new Set(validChatsAgentWithoutInteractionCritical)
      );
    }
  }, [allChats, userId]);

  useEffect(() => {
    if (!userId) return;
    storageHelper.set('filters-chat-attendance', filters, userId);
  }, [filters, userId]);

  useEffect(() => {
    if (!userId) return;
    storageHelper.set('sort-chat-attendance', sort, userId);
  }, [sort, userId]);

  const getQueuesChat = async (showIndividualQueue = false) => {
    if (queues.length) {
      if (showIndividualQueue) {
        return queues;
      } else {
        return queues.filter((queue) => queue?.name !== 'Usuario_Especifico');
      }
    }

    const response = await QueueService.getAll(
      undefined,
      undefined,
      false,
      undefined,
      {
        showAllQueues: true
      }
    );
    const attendantQueues = JSON.parse(localStorage.getItem('queues'));
    const result = response.data.data
      .filter(
        (fila) =>
          fila['is_chat'] &&
          attendantQueues.findIndex(
            (attendantQueue) => fila.id === attendantQueue.id
          ) >= 0
      )
      .sort((a, b) => a?.name?.localeCompare(b.name));

    setQueues(result);
    if (showIndividualQueue) return result;
    return result.filter((queue) => queue?.name !== 'Usuario_Especifico');
  };

  const getAllChatsSaved = () => {
    if (!userId || !allChats.length) return [];
    const savedChats = storageHelper.get('chatsSaved', userId) || [];
    return savedChats.filter((chatId) =>
      allChats.some((chat) => chat.id === chatId || chat._id === chatId)
    );
  };

  const getAllQueuesChat = async (showIndividualQueue = false) => {
    const response = await QueueService.getAll(
      undefined,
      undefined,
      false,
      undefined,
      {
        showAllQueues: true
      }
    );
    const result = response.data.data
      .filter((fila) => fila['is_chat'])
      .sort((a, b) => a?.name?.localeCompare(b.name));

    if (showIndividualQueue) return result;
    return result.filter((queue) => queue?.name !== 'Usuario_Especifico');
  };

  const saveChat = (id) => {
    if (!userId || !id) return;

    const chatExists = allChats.some(
      (chat) => chat.id === id || chat._id === id
    );

    if (!chatExists) {
      return;
    }

    setChatsSaved((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      storageHelper.set('chatsSaved', Array.from(newSet), userId);
      return newSet;
    });
  };

  const checkIfChatIsSaved = (id) => {
    if (!userId || !id || !allChats.length) return false;

    const chatExists = allChats.some(
      (chat) => chat.id === id || chat._id === id
    );
    if (!chatExists) return false;

    const savedChats = getAllChatsSaved();
    return savedChats.includes(id);
  };

  return (
    <QueueContentChatContext.Provider
      userId={userId}
      value={{
        chatsAgentWithoutInteractionCritical,

        saveChat,
        chatsSaved,
        setChatsSaved,
        checkIfChatIsSaved,
        filters,
        sort,
        setSort,
        setFilters,
        countChatsInAttendance,
        setCountChatsInAttendance,
        countChatsInWait,
        setCountChatsInWait,
        countAttributedChats,
        setCountAttributedChats,
        setAllChats,
        allChats,
        countersChatMetrics,
        setCounterChatMetrics,
        getQueuesChat,
        getAllQueuesChat
      }}
    >
      {children}
    </QueueContentChatContext.Provider>
  );
}

/**
 *
 * @typedef {Function} useQueueContentChatContext
 * @returns {{
 * saveChat: Function,
 * chatsSaved: Set,
 * setChatsSaved: Function,
 * checkIfChatIsSaved: Function,
 * filters: Object,
 * sort: Object,
 * setSort: Function,
 * setFilters: Function,
 * countChatsInAttendance: Number,
 * setCountChatsInAttendance: Function,
 * countChatsInWait: Number,
 * setCountChatsInWait: Function,
 * countAttributedChats: Number,
 * setCountAttributedChats: Function,
 * setAllChats: Function,
 * allChats: Array,
 * getQueuesChat: Function
 * getAllQueuesChat: Function,
 * countersChatMetrics: countersChatMetrics ,
 * setCounterChatMetrics: setCounterChatMetrics
 * }}
 */
export function useQueueContentChatContext() {
  const context = useContext(QueueContentChatContext);
  if (!context) {
    throw new Error(
      'useQueueContentChatContext must be used within a QueueContentChatProvider'
    );
  }
  return context;
}
